<template>
  <main class="flex flex-col gap-4 mb-10">
    <section class="flex justify-between items-center">
      <p class="text-lg font-bold">{{ $t("quiz.teacher.questions") }}</p>
      <div class="flex gap-3">
        <template v-if="hasGeneratedQuestions">
          <Button
            id="btn-generate-quiz"
            :label="$t('quiz.teacher.generateQuiz')"
            class="btn-primary p-button-sm"
            icon="pi pi-star-fill"
            :disabled="!quizId"
            :loading="generateQuizLoading"
            @click="generateQuiz()"
            v-if="role === 'TEACHER'"
          />
          <Button
            id="btn-save-all-questions"
            :label="
              $t('labels.generic', {
                pt: 'Salvar Questões',
                en: 'Save Questions',
              })
            "
            class="btn-success p-button-sm"
            icon="pi pi-save"
            :disabled="generatedQuestions.length === 0"
            :loading="savingAllQuestions"
            @click="saveAllGeneratedQuestions"
            v-if="role === 'TEACHER'"
          />
        </template>

        <template v-else-if="!loader">
          <Button
            id="btn-generate-quiz"
            :label="$t('quiz.teacher.generateQuiz')"
            class="btn-primary p-button-sm"
            icon="pi pi-star-fill"
            :disabled="!quizId"
            :loading="generateQuizLoading"
            @click="generateQuiz()"
            v-if="role === 'TEACHER' && questionsLocal.length === 0"
          />
          <Button
            id="btn-add-questions"
            :label="$t('quiz.teacher.add')"
            class="btn-primary p-button-sm"
            icon="pi pi-plus"
            :disabled="!quizId"
            @click="openQuestionOptions()"
            v-if="role === 'TEACHER'"
          />
        </template>
      </div>
    </section>

    <GeneratedQuizQuestions
      v-if="hasGeneratedQuestions"
      :questions="generatedQuestions"
      :role="role"
      :quizId="quizId"
      @questionsUpdated="onGeneratedQuestionsUpdated"
    />

    <div v-if="!hasGeneratedQuestions">
      <section
        class="flex flex-col md:flex-row justify-center md:justify-between items-center gap-3 bg-gray-200 font-bold p-4 rounded-md shadow-md"
      >
        <p>{{ $t("quiz.teacher.list") }}</p>
        <p v-if="!loader">
          {{ questionsLocal?.length }}
          {{ $t("labels.generic", { pt: "Questões", en: "Questions" }) }}
        </p>
      </section>

      <section v-if="!loader">
        <QuestionCard
          v-for="(question, index) in questionsLocal"
          :key="index"
          :question="question"
          :questionIndex="index"
          :loadBtn="loadBtn"
          :role="role"
          :isGeneratingQuiz="generateQuizLoading"
          @goToQuestion="goToQuestion"
          @deleteQuestion="deleteQuestion"
        />
      </section>

      <section
        class="py-10"
        v-if="!questionsLocal || (questionsLocal.length === 0 && !loader)"
      >
        <EmptyData
          v-if="!generateQuizLoading"
          :data="!questionsLocal || questionsLocal.length === 0"
          :label="emptyState.description"
          :image="emptyState.image"
          imgSize="h-24"
        />
      </section>
    </div>
  </main>

  <Loader :loader="loader" loaderSize="h-48" />

  <Dialog
    v-model:visible="generateQuizLoading"
    :draggable="false"
    :closable="false"
    :style="{ width: '400px' }"
    modal
  >
    <div class="flex flex-col items-center gap-4 py-8">
      <div
        class="animate-spin rounded-full h-12 w-12 border-b-2 border-h-primary-800"
      ></div>
      <p class="text-lg text-gray-600">
        {{
          $t("labels.generic", {
            pt: "Gerando questões...",
            en: "Generating questions...",
          })
        }}
      </p>
      <p class="text-sm text-gray-500">
        {{ $t("labels.generic", { pt: "Aguarde...", en: "Please wait..." }) }}
      </p>
    </div>
  </Dialog>

  <Dialog
    v-model:visible="questionDialog"
    :draggable="false"
    :breakpoints="{ '960px': '95vw', '640px': '95vw' }"
    :style="{ width: '35vw', minWidth: '360px' }"
    @hide="closeQuestionOptions"
    modal
  >
    <template #header>
      <p class="text-lg font-bold w-full text-center">
        {{
          $t("labels.generic", {
            pt: "Criar nova questão",
            en: "Create new question",
          })
        }}
      </p>
    </template>

    <div id="question-types" class="border-t pt-8">
      <div class="w-full grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div v-for="(type, index) in questionTypes" :key="index" class="w-full">
          <RadioButton
            v-model="questionTypeSelected"
            :inputId="type.value"
            :value="type"
            class="!hidden"
          />

          <div class="grid">
            <label
              :for="type.value"
              :class="[
                type.value === questionTypeSelected?.value &&
                  'bg-h-primary-50 border-2 border-h-primary-700',
                ' rounded p-5 text-center cursor-pointer border border-h-primary-400 text-h-primary-800 hover:opacity-75 transition-all flex items-center gap-3',
              ]"
            >
              <v-icon :name="type.icon" />
              <p>{{ type.name }}</p>
            </label>
          </div>
        </div>
      </div>

      <div
        v-if="questionTypeSelected?.info"
        class="bg-gray-50 mt-5 p-5 text-center font-light"
      >
        {{ questionTypeSelected?.info }}
      </div>

      <div
        id="buttons"
        v-if="questionTypeSelected?.value || questionTypeSelected?.value === 0"
        class="flex justify-end border-t mt-5 pt-5 gap-3"
      >
        <Button
          class="btn-outline p-button-sm"
          @click="closeQuestionOptions"
          :disabled="generateQuizLoading"
        >
          {{ $t("labels.cancel") }}
        </Button>
        <Button
          :class="`btn-primary p-button-sm ${
            questionTypeSelected?.value === '' && 'disabled'
          }`"
          @click="chooseQuestionType()"
          :disabled="generateQuizLoading"
        >
          {{ $t("labels.proceed") }}
        </Button>
      </div>
    </div>
  </Dialog>
</template>

<script lang="ts">
import {
  Question,
  QuestionDragDrop,
  QuestionGap,
  QuestionTrueFalse,
} from "@/models/quiz.model";

import { QuizRest } from "@/services/rest/quiz.rest";
import { GenerateQuizRest } from "@/services/rest/generate-quiz.rest";
import { QuestionType } from "@/store/quiz-questions";
import { GenericsUtils } from "@/utils/generics.util";
import { GoToRoute } from "@/utils/go-to.util";
import { delay } from "rxjs";
import { defineAsyncComponent, defineComponent, PropType } from "vue";
import { ObjectiveService } from "../../objective.service";
import { ToastFactory } from "@/utils/toast-factory.util";
import { Messages } from "@/utils/messages.util";

const QuestionCard = defineAsyncComponent(() => import("./questionCard.vue"));
const GeneratedQuizQuestions = defineAsyncComponent(
  () => import("../GenerateQuiz/GenerateQuizQuestions.vue")
);

export default defineComponent({
  name: "formQuestions",
  components: {
    QuestionCard,
    GeneratedQuizQuestions,
  },
  computed: {
    rest(): QuizRest {
      return new QuizRest();
    },
    generateQuizRest(): GenerateQuizRest {
      return new GenerateQuizRest();
    },
    service(): ObjectiveService {
      return new ObjectiveService();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
    generics(): GenericsUtils {
      return new GenericsUtils();
    },
    hasGeneratedQuestions(): boolean {
      return this.generatedQuestions.length > 0;
    },
  },
  props: {
    quizId: {
      type: String,
      default: "",
    },
    role: [String],
    questions: {
      type: Array as PropType<any>,
      default: [],
    },
    objective: [Object],
  },
  data() {
    return {
      questionsLocal: [] as Array<
        Question | QuestionTrueFalse | QuestionDragDrop | QuestionGap
      >,
      generatedQuestions: [] as Array<any>,
      savingAllQuestions: false as boolean,
      loadBtn: [] as any,
      loader: true as boolean,
      generateQuizLoading: false as boolean,
      emptyState: {
        image: "empty_questions.svg",
        description: this.$t("quiz.teacher.empty"),
      } as any,
      questionTypes: [
        {
          name: this.$t("quiz.teacher.types.multiple"),
          value: String(QuestionType.DEFAULT),
          icon: "md-checklistrtl",
          info: this.$t("quiz.teacher.typesInfo.multiple"),
        },
        {
          name: this.$t("quiz.teacher.types.trueFalse"),
          value: String(QuestionType.TRUE_OR_FALSE),
          icon: "md-libraryaddcheck-outlined",
          info: this.$t("quiz.teacher.typesInfo.trueFalse"),
        },
        {
          name: this.$t("quiz.teacher.types.dragAndDrop"),
          value: String(QuestionType.DRAG_AND_DROP),
          icon: "md-copyall",
          info: this.$t("quiz.teacher.typesInfo.dragAndDrop"),
        },
        {
          name: this.$t("quiz.teacher.types.gap"),
          value: String(QuestionType.FILL_IN_THE_BLANKS),
          icon: "co-text-shapes",
          info: this.$t("quiz.teacher.typesInfo.gap"),
        },
      ],
      questionDialog: false,
      questionTypeSelected: {} as any,
      updateDeletedQuestion: this.updateQuestionState as Function,
    };
  },
  mounted() {
    setTimeout(() => {
      this.questionsLocal = this.questions;
      this.loader = false;
    }, 500);
  },
  methods: {
    goToQuestion(id?: string, type?: any): void {
      const params = {
        quizId: this.quizId,
        objectiveId: this.objective?.id,
        id: id ?? undefined,
      } as any;

      if (type) {
        localStorage.setItem("question-type", QuestionType[type]);
      }
      this.$router.push({
        name: "formQuestion",
        params,
        state: { paramsQuiz: this.$route.params },
      });
    },

    deleteQuestion(question: any): void {
      this.loadBtn[question?.index] = true;

      const deleteMethod =
        question?.type === QuestionType.DEFAULT || question?.type === undefined
          ? this.rest.deleteDefaultQuestion.bind(this.rest)
          : this.rest.deleteQuestion.bind(this.rest);

      deleteMethod(this.quizId, question.id)
        .pipe(delay(500))
        .subscribe({
          next: () => {
            this.loadBtn[question.index] = false;
            this.questionsLocal = this.questionsLocal.filter(
              (e: any) => e.id !== question.id
            ) as any[];
            this.$toast.add(ToastFactory.success(Messages.QUESTION_DELETED));
            this.updateDeletedQuestion(this.questionsLocal);
          },
          error: () => {
            this.loadBtn[question.index] = false;
          },
          complete: () => {
            this.loadBtn[question.index] = false;
          },
        });
    },

    openQuestionOptions(): void {
      this.questionDialog = true;
    },

    closeQuestionOptions(): void {
      this.questionDialog = false;
      this.questionTypeSelected = null;
    },

    chooseQuestionType(): void {
      if (!this.questionTypeSelected) return;
      this.questionDialog = false;
      this.goToQuestion();
      localStorage.setItem("question-type", this.questionTypeSelected?.value!);
      localStorage.setItem("chapterId", this.$route.params.chapterId as string);
    },

    generateQuiz(): void {
      this.generateQuizLoading = true;

      const data = {
        learning_object_id: this.objective?.id,
        remake: false,
      };

      this.generateQuizRest
        .postGenerateQuiz(data)
        .then((response) => {
          if (response && Array.isArray(response)) {
            this.generatedQuestions = response.map((question, qIndex) => {
              return {
                ...question,
                type: question.type.toUpperCase().replace(/ /g, "_"),
              };
            });
            this.$toast.add(
              ToastFactory.success(
                this.$t("labels.generic", {
                  pt: "Quiz gerado com sucesso!",
                  en: "Quiz generated successfully!",
                })
              )
            );
          }
        })
        .catch((error) => {
          this.$toast.add(
            ToastFactory.error(
              this.$t("labels.generic", {
                pt: "Erro ao gerar quiz. Tente novamente.",
                en: "Error generating quiz. Please try again.",
              })
            )
          );
        })
        .finally(() => {
          this.generateQuizLoading = false;
        });
    },

    onGeneratedQuestionsUpdated(questions: any[]): void {
      this.generatedQuestions = questions;
    },

    saveAllGeneratedQuestions(): void {
      this.savingAllQuestions = true;
      const questionsToSave = JSON.parse(
        JSON.stringify(this.generatedQuestions)
      );

      this.generateQuizRest
        .postSaveAllQuestions(this.quizId, questionsToSave)
        .pipe(delay(500))
        .subscribe({
          next: (response) => {
            this.savingAllQuestions = false;
            this.$toast.add(
              ToastFactory.success(
                this.$t("labels.generic", {
                  pt: "Todas as questões foram salvas com sucesso!",
                  en: "All questions have been saved successfully!",
                })
              )
            );
            this.reloadQuestions();
          },
          error: (error) => {
            this.savingAllQuestions = false;
            this.$toast.add(
              ToastFactory.error(
                this.$t("labels.generic", {
                  pt: "Erro ao salvar questões. Tente novamente.",
                  en: "Error saving questions. Please try again.",
                })
              )
            );
          },
        });
    },

    reloadQuestions(): void {
      this.loader = true;
      this.generatedQuestions = [];

      const params = {
        page: 0,
        size: 100,
      };

      Promise.all([
        this.rest.getQuestionsDefaultByQuiz(this.quizId, params).toPromise(),
        this.rest.getQuestionsByQuiz(this.quizId, params).toPromise(),
      ])
        .then(([apiQuizResponse, apiV2Response]) => {
          const apiQuizQuestions = apiQuizResponse?.content || [];
          const apiV2Questions = apiV2Response?.content || [];

          this.questionsLocal = [...apiQuizQuestions, ...apiV2Questions];
          this.updateDeletedQuestion(this.questionsLocal);
          this.loader = false;
        })
        .catch((error) => {
          console.error("Erro ao carregar questões:", error);
          this.loader = false;
        });
    },
  },
  inject: ["updateQuestionState"],
});
</script>

<style scoped>
.btn-success {
  @apply bg-green-600 hover:bg-green-700 border-green-600 text-white;
}
</style>
